import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * useGoBack is used to enable "go back" functionality.
 * it handles the case where the current location does not allow going back because it is directly navigated to
 * by allowing you to specify a fallback URL to navigate to.
 *
 * to allow a page to be navigated back to, set the `canGoBack` location state to true when navigating away the page.
 * e.g. if you are on page A and want to navigate to page B and wants to allow going back to A from B, set canGoBack location state to true when navigating to B.
 *
 * @param fallbackUrl the URL to navigate to if the current location does not allow going back.
 * @returns a function that navigates back or to a fallback URL if the current location does not allow going back.
 */
export function useGoBack(fallbackUrl: string) {
    const navigate = useNavigate();
    const location = useLocation();
    return useCallback(() => {
        if (location.state?.canGoBack) {
            navigate(-1);
        } else {
            navigate(fallbackUrl, { replace: true });
        }
    }, [fallbackUrl, location, navigate]);
}
